import { z } from 'zod';
import { httpUrlSchema, isoDateTimeSchema, quantitySchema, requiredText, } from '../common/coercion.js';
import { janisAmountSchema } from './order.js';
/**
 * GET jef/api/invoice/{id} y cada fila de GET jef/api/invoice. Verificado en el
 * OpenAPI JEF el 2026-09-23; `orderIds`, el 2026-10-20 contra jef 2.41.0
 * (`test/fixtures/janis/reales/jef-invoice-spec.json`).
 */
export const janisInvoiceSchema = z
    .object({
    id: requiredText(64),
    number: requiredText(32),
    // JEF admite una factura sin URL; no inventamos un documento descargable.
    url: z.string().nullish(),
    issueDate: isoDateTimeSchema.optional(),
    /**
     * Los pedidos de la factura: un ARREGLO, no `orderId`. El spec no lo marca
     * requerido. Con el, la barrera de `registerInvoice` distingue una factura
     * de ESTE pedido de otra con el mismo numero.
     */
    orderIds: z.array(z.string()).nullish(),
})
    .passthrough();
/**
 * `POST /invoice` del **Janis Economy and Finance Service (JEF)** (F6).
 *
 * Hallazgo que resolvio F6: **Janis NO emite DTE chileno.** JEF registra
 * facturas emitidas por un externo, con su numero, su valor y la URL del
 * documento electronico. El campo fiscal nativo (`electronicInvoiceAccessKey`)
 * es solo para NF-e de Brasil.
 *
 * VERIFICADO contra el OpenAPI publico de JEF el 2026-10-07
 * (https://docs.janis.in/versions/v2/services/openapi/jef.json). El body es un
 * `oneOf` de tres variantes; emitimos SOLO `InvoiceWithOrderId`:
 *
 * - `currency` es **readOnly** en el spec y el validador de Janis rechaza las
 *   claves que no espera: mandarla daba 400. Ya no viaja.
 * - Los items del spec son `{ id, quantity, value }` con el `id` de la linea del
 *   **invoice-form**, no el SKU. Sin el formulario no se pueden armar, y con
 *   `orderId` no hacen falta: JEF factura el pedido completo.
 * - Mandar `orderId` y `orderCommerceId` a la vez calzaria dos ramas del
 *   `oneOf`. Va solo `orderId`, que ademas tiene precedencia.
 *
 * 🔴 **Precondicion documentada por Janis**: _"Para poder facturar, sea manual
 * o por integracion, el pedido debe estar en estado Pickeado"_
 * (https://docs.janiscommerce.com/docs/facturación-manual). Por eso F6 registra
 * la factura recien con el `ready-for-invoice` (o el ready-check de su
 * reconciliacion), nunca al recibir el pedido.
 */
export const janisInvoiceItemSchema = z
    .object({
    /** Id de la linea en el invoice-form (`GET /invoice-form`), no el SKU. */
    id: requiredText(64),
    quantity: quantitySchema,
    /** Precio unitario. */
    value: janisAmountSchema,
})
    .strict();
/**
 * Numero de una factura en JEF: `<tipo>-<folio>` y, en una venta agrupada, la
 * parte del hijo: `<tipo>-<folio>-<n>`, con `n` de 1 a 999. Ni el folio ni la
 * parte llevan ceros a la izquierda: es exactamente lo que arma
 * `janisInvoiceNumber` y lo que reconoce `parseJanisInvoiceNumber` (y el CHECK
 * de `dte_order_share.jef_number`). Lo emitimos nosotros, asi que estrecharlo
 * no rompe a nadie.
 *
 * ## Venta agrupada (`docs/pedidos-de-tienda.md` §1.4)
 *
 * Un DTE por venta, y cada pedido hijo registra SU parte en su propio
 * `ready-for-invoice`: `orderId` = el hijo y `value` = sus lineas mas su
 * `DESPACHO`. ✅ QA 2026-10-28 (`test/fixtures/janis/grupo-2026-10/`): JEF acepta
 * `33-<folio>-1` y `33-<folio>-2`, un `invoice-form` por hijo, y cada hijo pasa a
 * `invoiced`.
 *
 * 🔴 **JEF NO exige que el numero sea unico**: en QA acepto `33-<folio>-1` en un
 * tercer pedido y quedaron dos facturas con el mismo numero. `filters[number]`
 * de `GET jef /invoice` es **exacto** (a diferencia del de `/refund`) y cada
 * fila trae `orderIds`, asi que la barrera del middleware —buscar el numero
 * antes del POST y no registrar encima del de otro pedido— es lo UNICO que lo
 * impide. En una venta agrupada, ademas, se revisa que `<tipo>-<folio>` sin
 * sufijo no este registrado (un registro manual del DTE completo).
 */
export const JANIS_INVOICE_NUMBER_PATTERN = /^(33|39)-[1-9]\d{0,17}(-[1-9]\d{0,2})?$/;
/** La parte mas alta de una venta agrupada: tres digitos. */
export const JANIS_INVOICE_MAX_SHARE = 999;
export const janisInvoiceCreateRequestSchema = z
    .object({
    /**
     * Numero del DTE: `<tipo>-<folio>` (p. ej. `39-900000001`), o
     * `<tipo>-<folio>-<n>` para la parte de un hijo (ver
     * `JANIS_INVOICE_NUMBER_PATTERN`). Es la clave con la que se busca en JEF
     * antes del POST: un numero sin el tipo no encontraria el registro previo y
     * duplicaria la factura. Una boleta y una factura pueden compartir folio;
     * con el tipo adelante, no chocan.
     */
    number: z
        .string()
        .regex(JANIS_INVOICE_NUMBER_PATTERN, 'El numero de la factura va como <tipo>-<folio> (39-900000001) o, en una venta ' +
        'agrupada, <tipo>-<folio>-<n> (39-900000001-2).'),
    /** Id del pedido en Janis (OMS). */
    orderId: requiredText(64),
    issueDate: isoDateTimeSchema,
    value: janisAmountSchema,
    /** URL del documento electronico. Es lo que despues se sube a Multivende. */
    url: httpUrlSchema.optional(),
    isPartialInvoice: z.boolean().optional(),
})
    // EMITIDO por nosotros hacia JEF: estricto.
    .strict();
/** Respuesta de `POST /invoice`: el id de la factura en JEF. */
export const janisInvoiceCreatedSchema = z.object({ id: requiredText(64) }).passthrough();
/**
 * Arma el numero con que se registra una factura en JEF: `<tipo>-<folio>` o, con
 * `share`, `<tipo>-<folio>-<n>`. Lanza si el folio no es un entero positivo
 * seguro o si la parte no es un entero de 1 a 999: un numero mal armado no se
 * encontraria en la barrera y se registraria dos veces.
 */
export function janisInvoiceNumber(documentType, folio, share) {
    if (!Number.isSafeInteger(folio) || folio <= 0) {
        throw new Error(`El folio de la factura debe ser un entero positivo (${String(folio)}).`);
    }
    if (share !== undefined &&
        share !== null &&
        (!Number.isInteger(share) || share < 1 || share > JANIS_INVOICE_MAX_SHARE)) {
        throw new Error(`La parte de una venta agrupada va de 1 a ${String(JANIS_INVOICE_MAX_SHARE)} (${String(share)}).`);
    }
    const base = `${String(documentType)}-${String(folio)}`;
    const numero = share === undefined || share === null ? base : `${base}-${String(share)}`;
    return janisInvoiceCreateRequestSchema.shape.number.parse(numero);
}
/**
 * Separa un numero de factura de JEF en tipo, folio y parte, o `null` si no es
 * uno que el middleware pueda haber armado.
 *
 * 🔴 **Nunca parte a ciegas por el guion**: un numero de venta tambien lleva
 * guiones (`PV-344907-2`), y cortar por el ultimo convertiria la venta en un
 * folio con parte. Solo devuelve algo si el texto es EXACTAMENTE lo que
 * `janisInvoiceNumber` produce para esas partes (sin espacios, sin ceros a la
 * izquierda y con un folio que cabe en un entero seguro); si no, `null`.
 */
export function parseJanisInvoiceNumber(numero) {
    const partes = /^(33|39)-([1-9]\d{0,17})(?:-([1-9]\d{0,2}))?$/.exec(numero);
    if (partes === null) {
        return null;
    }
    const documentType = partes[1] === '33' ? 33 : 39;
    const folio = Number(partes[2]);
    const share = partes[3] === undefined ? null : Number(partes[3]);
    if (!Number.isSafeInteger(folio)) {
        return null;
    }
    // La vuelta completa: lo que no se arma igual no se reconoce.
    if (janisInvoiceNumber(documentType, folio, share) !== numero) {
        return null;
    }
    return { documentType, folio, share, base: `${String(documentType)}-${String(folio)}` };
}
//# sourceMappingURL=invoice.js.map